import * as XLSX from 'xlsx';
import { getProperty, formatDate } from './helper';
import i18n from '../../old/libs/language';
import { extractionListService } from '@old/services/domain/base/silobag/extraction/extraction-list';
import { listSilobagService } from '@old/services/domain/base/silobag/base/silobag-list';
import { stockStatusListService } from '@old/services/domain/page/report/status-stock/report-status-stock.info';
import { listCountrysideService } from '@old/services/domain/base/countryside/countryside-list';
import { useGlobalStore } from '@old/global-store/use-store';
import * as HOOKS from '@siloreal:shared:hooks';

const determineSilobagStatus = (isActive: boolean, isPartiallyExtracted: boolean) => {
  if (isActive && !isPartiallyExtracted) {
    return i18n.t('app.active');
  } else if (isActive && isPartiallyExtracted) {
    return i18n.t('app.partially_extracted');
  } else {
    return i18n.t('app.closed');
  }
};

type SummarySilobagList = Awaited<ReturnType<typeof listSilobagService>>['summary']['data'];

export const GenerateSilobagExcelData = (data: SummarySilobagList, FileName: string) => {
  const columnMapping: Record<string, string> = {
    id: 'ID',
    name: i18n.t('app.name'),
    'organizations.name': `${i18n.t('app.organization')}`,
    bagging_date: i18n.t('app.silobag_data.bagging_date2'),
    created_at: i18n.t('app.register_date'),
    'silobag_species_types.name': i18n.t('app.silobag_data.species'),
    'silobag_product_types.name': i18n.t('app.silobag_data.product'),
    variety: `${i18n.t('app.silobag_data.variety')} (%)`,
    main_lat: i18n.t('app.silobag_data.lat'),
    main_lon: i18n.t('app.silobag_data.long'),
    input_weight: `${i18n.t('app.silobag_data.weight_bagging')} (Kg)`,
    remaining_stock: `${i18n.t('app.field_weigth')} (Kg)`,
    total_extracted_weight: `${i18n.t('app.total_extracted_weigth')}`,
    Diferencia: `${i18n.t('app.silobag_detail.difference')}`,
    initial_humidity: `${i18n.t('app.state_stock_page.humidity')} (%)`,
    size: `${i18n.t('app.silobag_data.size')} (m)`,
    diameter: `${i18n.t('app.silobag_data.diameter')}`,
    'silobag_harvests.name': `${i18n.t('app.silobag_data.harvest')}`,
    'lots.countrysides.name': `${i18n.t('app.countryside')}`,
    'lots.countrysides.countryside_zone.name': `${i18n.t('app.countryside_zone_modal.name_title')}`,
    'lots.name': `${i18n.t('app.silobag_data.lot_stock')}`,
    lote_origen: `${i18n.t('app.silobag_data.lot_origin')}`,
    responsible_for_bagging: `${i18n.t('app.silobag_data.bagging_responsible')}`,
    silobag_brand: `${i18n.t('app.slobag_brand')}`,
    'humidity_sources.description': `${i18n.t('app.humidity_sources')}`,
    'weight_sources.description': `${i18n.t('app.weight_sources')}`,
    // 'silobag_data_statuses.name': 'Estado de Carga',
    fillying_coefficient: `${i18n.t('app.fillying_coefficient')}`,
    calculated_coefficient: `${i18n.t('app.calculated_coefficient')}`,
    observations: `${i18n.t('app.observations')}`,
    silobag_status: `${i18n.t('app.silobag_status')}`,
    'silobag_data_statuses.id': `${i18n.t('app.silobag_data_statuses')}`,
    '_count.lab_protocols': `${i18n.t('app.silobag_lab_protocols')}`,
    'lab_protocols.created_at': `${i18n.t('app.lab_protocols_created_at')}`,
    last_extraction_date: i18n.t('app.lab_protocols_closed_date'),
  };
  const extractedData: unknown[] = [];

  data.forEach((item) => {
    // Create an extracted item for each item in filteredData
    const extractedItem: { [key: string]: unknown } = {};

    for (const property in columnMapping) {
      const value = getProperty(item, property);

      if (
        // property === "confection_date" ||
        property === 'created_at' ||
        property === 'bagging_date'
      ) {
        extractedItem[columnMapping[property]] = value ? formatDate(value) : '—';
      } else if (property === 'silobags_origin_lots.lots') {
        // Handle the special case as needed
      } else if (property === 'silobags_devices.devices') {
        // Handle the special case as needed
      } else if (
        property === 'input_weight' ||
        property === 'remaining_stock' ||
        property === 'total_extracted_weight'
      ) {
        // Handle numeric values
        const formattedValue =
          value !== undefined && value !== null && value !== '' ? parseFloat(value) : '—';
        extractedItem[columnMapping[property]] = formattedValue;
      } else if (
        property === 'initial_humidity' ||
        property === 'varietal_purity' ||
        property === 'split_grain_percentage'
      ) {
        // Handle numeric values
        const formattedValue =
          value !== undefined && value !== null && value !== '' ? parseFloat(value) : '—';
        extractedItem[columnMapping[property]] = formattedValue;
      } else if (property === 'silobag_status') {
        const silobagStatus = determineSilobagStatus(item.active, item.partially_extracted!);
        extractedItem[columnMapping[property]] = silobagStatus;
      } else if (property === 'silobag_data_statuses.id') {
        const silobagStatus = item?.silobag_data_statuses?.id === 1 ? i18n.t('app.no') : i18n.t('app.yes');
        extractedItem[columnMapping[property]] = silobagStatus;
      } else if (property === '_count.lab_protocols') {
        extractedItem[columnMapping[property]] = item._count?.lab_protocols || 0;
      } else if (property === 'lab_protocols.created_at') {
        extractedItem[columnMapping[property]] =
          item.lab_protocols && item.lab_protocols.length > 0
            ? formatDate(item.lab_protocols[0].created_at)
            : '—';
      } else if (property === 'last_extraction_date') {
        const targetDate = item.last_extraction_date;
        const dateDisplayed = targetDate ? formatDate(targetDate) : '—';
        extractedItem[columnMapping[property]] = dateDisplayed;
      } else if (property === 'silobag_species_types.name') {
        extractedItem[columnMapping[property]] =
          item.silobag_species_types.silobag_species_types_translations[0].description;
      } else if (property === 'silobag_product_types.name') {
        extractedItem[columnMapping[property]] =
          item.silobag_product_types.silobag_product_types_translations[0].description;
      } else {
        // Handle other properties
        extractedItem[columnMapping[property]] =
          value !== undefined && value !== null && value !== Infinity && value !== '' ? value : '-';
      }
    }

    const inputWeight = getProperty(item, 'input_weight');
    const size = getProperty(item, 'size');

    const totalExtractedWeight = getProperty(item, 'total_extracted_weight');
    const diferencia =
      inputWeight !== '—' && totalExtractedWeight !== '—' ? inputWeight - totalExtractedWeight : '—';
    extractedItem[`${i18n.t('app.silobag_detail.difference')}`] = diferencia;

    const calculated_coefficient = parseFloat(inputWeight) / parseFloat(size);

    extractedItem[`${i18n.t('app.calculated_coefficient')}`] =
      !Number.isNaN(calculated_coefficient) && calculated_coefficient !== Infinity
        ? calculated_coefficient.toFixed(0)
        : '-';

    const siloOriginLots = item.silobags_origin_lots.map((item) =>
      item && item.lots ? item.lots.name : null
    );

    const loteOrigen = siloOriginLots.join(', ');

    extractedItem[`${i18n.t('app.silobag_data.lot_origin')}`] = loteOrigen;

    extractedData.push(extractedItem);
  });

  const ws = XLSX.utils.json_to_sheet(extractedData, {
    header: Object.values(columnMapping),
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Silobags');
  XLSX.writeFile(wb, FileName + '.xlsx');
};

type ExtractionListServiceType = Awaited<ReturnType<typeof extractionListService>>['summary'];
type DataType = ExtractionListServiceType[0];
type ItemType = ExtractionListServiceType[0]['silobags']['silobags_origin_lots'][0];

export const GenerateExtractionsExcelData = async (response: ExtractionListServiceType, siloID?: string) => {
  const extractionData = siloID ? response.filter((data: DataType) => data.silobags.id === siloID) : response;

  const tabla = [
    {
      A: 'ID',
      B: i18n.t('app.silobag_name'),
      C: i18n.t('app.organization'),
      D: i18n.t('app.countryside'),
      Z: i18n.t('app.countryside_zone_modal.name_title'),
      E: i18n.t('app.silobag_data.lot_stock'),
      F: i18n.t('app.silobag_data.lot_origin'),
      G: i18n.t('app.silobag_data.specie'),
      H: i18n.t('app.state_stock_page.humidity'),
      I: i18n.t('app.status'),
      J: i18n.t('app.silobags_extractions.extraction_date'),
      K: i18n.t('app.public_pages.user'),
      L: i18n.t('app.silobags_extractions.freight_brok'),
      M: i18n.t('app.silobags_extractions.transport'),
      N: i18n.t('app.silobags_extractions.driver'),
      O: i18n.t('app.silobags_extractions.chassis_patent'),
      P: i18n.t('app.silobags_extractions.chassis_attached'),
      Q: i18n.t('app.silobags_extractions.ctg'),
      R: i18n.t('app.silobags_extractions.cp'),
      S: i18n.t('app.silobags_extractions.contract'),
      T: i18n.t('app.silobags_extractions.destination'),
      U: i18n.t('app.silobags_extractions.gross_weight'),
      V: i18n.t('app.silobags_extractions.tara'),
      W: i18n.t('app.silobags_extractions.liquid'),
      X: i18n.t('app.observations'),
      Y: i18n.t('app.silobag_data_statuses'),
    },
  ];

  extractionData.forEach((silos: DataType) => {
    const siloOriginLots = silos.silobags.silobags_origin_lots.map((item: ItemType) => item.lots?.name);
    const loteOrigen = siloOriginLots.join(', ');

    tabla.push({
      A: silos.silobags.id,
      B: silos.silobags?.name,
      C: silos.silobags.organizations.name,
      D: silos.silobags.countrysides.name,
      Z: silos.silobags.countrysides.countryside_zone?.name || '',
      E: silos.silobags.lots.name,
      F: loteOrigen,
      G: silos.silobags.silobag_species_types.silobag_species_types_translations[0].description,
      H: silos.silobags.initial_humidity?.toString() || '',
      I: silos.silobags.active ? 'Activo' : 'Cerrado',
      J: formatDate(silos.extraction_date.toString())?.toString() || '',
      K: silos.users.fullname,
      L: silos.freight_transport || '',
      M: silos.carrier || '',
      N: silos.driver || '',
      O: silos.back_plate || '',
      P: silos.trailer_back_plate || '',
      Q: silos.ctg_document || '',
      R: silos.waybill || '',
      S: silos.contract || '',
      T: silos.destination || '',
      U: silos.gross_weight?.toString() || '',
      V: silos.truck_weight?.toString() || '',
      W: silos.weight?.toString() || '',
      X: silos.observations || '',
      Y: silos.silobags.silobag_data_status_id === 1 ? i18n.t('app.no') : i18n.t('app.yes'),
    });
  });

  const dataFinal = [...tabla];

  const libro = XLSX.utils.book_new();
  const hoja = XLSX.utils.json_to_sheet(dataFinal, { skipHeader: true });

  XLSX.utils.book_append_sheet(libro, hoja, 'Extracciones');

  XLSX.writeFile(libro, i18n.t('app.silobags_extractions.extractions') + '.xlsx');
};
//Tipos para exportación de Reporte de Estado de Stock
type StockStatusListServiceType = Awaited<ReturnType<typeof stockStatusListService>>['summary']['rows'];
type CountrysidesType = Awaited<ReturnType<typeof listCountrysideService>>['summary'];
type TypeFormatDate = Parameters<typeof formatDate>[0];

export const GenerateReportStateStockExcelData = (
  data: StockStatusListServiceType,
  countrysides?: CountrysidesType
) => {
  const {
    organizationId,
    filtersReportStateStock,
    selectedOrganizationName: organizationName,
  } = useGlobalStore.getState();
  const countrysideIdReport = filtersReportStateStock.countrysideId;

  const statusMap: Record<string | number, string> = {
    1: 'Optimo',
    2: 'Precaucion',
    4: 'Critico',
  };

  const i18n_new = HOOKS.useLocale.getState();

  const columns = [
    { key: 'id', label: 'IdSilo' },
    { key: 'silobag_name', label: 'Silo' },
    { key: 'countryside_name', label: i18n.t('app.countrysides') },
    { key: 'lot_name', label: i18n.t('app.silobag_data.lot_stock') },
    { key: 'remaining_stock', label: i18n.t('app.silobag_data.remaining_stock') + ' (kg)' },
    {
      key: 'bagging_date',
      label: i18n.t('app.state_stock_page.date_bagging'),
      format: (v: TypeFormatDate) => (v ? formatDate(v) : '—'),
    },
    { key: 'days_since_last_poe', label: 'Ultima visita (dias)' },
    {
      key: 'first_poe_status_id',
      label: i18n.t('app.state_stock_page.date_poe'),
      format: (v: string | number | null) => {
        if (v == null || v === '') return '—';
        return statusMap[v] || v;
      },
    },
    {
      key: 'second_poe_status_id',
      label: 'Penultima visita',
      format: (v: string | number | null) => {
        if (v == null || v === '') return '—';
        return statusMap[v] || v;
      },
    },
    {
      key: 'last_humidity_revision_value',
      label: `${i18n_new.t('temp.common.last_calado_humidity')} (%)`,
      format: (v: string | number | null) => {
        if (v == null || v === '') return '—';
        return v;
      },
    },
    {
      key: 'last_humidity_revision_date',
      label: i18n_new.t('temp.common.last_calado_date'),
      format: (v: TypeFormatDate) => (v ? formatDate(v) : '—'),
    },
    {
      key: 'worse_humidity_revision_value',
      label: `${i18n_new.t('temp.common.max_quality_revision_humidity')} (%)`,
      format: (v: string | number | null) => {
        if (v == null || v === '') return '—';
        return v;
      },
    },
    {
      key: 'worse_humidity_revision_date',
      label: i18n_new.t('temp.common.max_quality_revision_humidity_date'),
      format: (v: TypeFormatDate) => (v ? formatDate(v) : '—'),
    },
    { key: 'initial_humidity', label: i18n.t('app.silobag_data.initial_humidity') + ' (%)' },
    {
      key: 'first_sbd_first_measurement_heg',
      label: '1 - ' + i18n.t('app.state_stock_page.humidity') + '(%)',
    },
    {
      key: 'second_sbd_first_measurement_heg',
      label: '2 - ' + i18n.t('app.state_stock_page.humidity') + '(%)',
    },
    {
      key: 'third_sbd_first_measurement_heg',
      label: '3 - ' + i18n.t('app.state_stock_page.humidity') + '(%)',
    },
  ];

  const excelData = data.map((row) => {
    const obj: Record<string, unknown> = {};
    columns.forEach((col) => {
      const value = row[col.key as keyof typeof row];
      obj[col.label] = col.format ? col.format(value as string | null) : (value ?? '—');
    });
    return obj;
  });

  const ws = XLSX.utils.json_to_sheet(excelData, {
    header: columns.map((col) => col.label),
  });
  const OrganizationInFile = organizationId ? organizationName : '';
  const countrysideInFile = countrysideIdReport
    ? countrysides?.find((c) => c.id === countrysideIdReport)?.name || ''
    : '';
  const today = new Date();
  const dateInFile = formatDate(today);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Estado Stock');
  XLSX.writeFile(
    wb,
    'EstadoDeStock ' + OrganizationInFile + ' ' + countrysideInFile + ' ' + dateInFile + '.xlsx'
  );
};

export const downloadBlob = (blob: Blob, date?: string, fileName?: string) => {
  if (!blob || !(blob instanceof Blob)) {
    return;
  }

  if (blob.size === 0) {
    return;
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  const fileToDownload = fileName + '—' + formatDate(date);
  link.download = fileToDownload + '.pdf';
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, 100);
};
